// Skills (rbac-restore R3 3.4) over gateway/api/skills.py.
//
//   GET   /workspaces/{id}/skills                 view_skills    own + active platform skills
//   GET   /workspaces/{id}/skills/{sid}/export    view_skills    SKILL.md download
//   POST  /workspaces/{id}/skills                 manage_skills + skills.workspace
//   POST  /workspaces/{id}/skills/import          manage_skills + skills.workspace  {skill_md}
//   POST  /workspaces/{id}/skills/fork            manage_skills + skills.workspace  {platform_skill_id, slug?}
//   PATCH /workspaces/{id}/skills/{sid}           manage_skills + skills.workspace  {status}
//
// Skills are playbook text: they never execute or grant anything.
import { useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiFetch, apiUrl } from '../api/client'
import { errorMessage } from '../api/errors'
import { qk } from '../api/queries'
import type {
  SkillCreateRequest,
  SkillForkRequest,
  SkillStatus,
  SkillSummary,
  SkillsResponse,
  SkillWriteResult,
} from '../api/types'
import { useSession } from '../context/SessionContext'
import { useCan } from '../rbac'
import { useFeature, type Gate } from '../entitlements'
import { PlanNote } from '../entitlements/PlanNote'
import { Icon } from '../components/Icon'
import { Modal } from '../components/Modal'
import { formatDate } from '../components/format'
import { Badge, EmptyState, PageHeader, QueryState } from '../components/ui'

// gateway/skills/constants.py SLUG_PATTERN / STATUS_TRANSITIONS; the server re-checks
// (422 / 409). Offering only legal moves keeps the select from inviting a 409.
const SLUG_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/
const NEXT_STATUS: Record<SkillStatus, SkillStatus[]> = {
  draft: ['active', 'archived'],
  active: ['archived'],
  archived: ['active'],
}
const STATUS_LABEL: Record<SkillStatus, string> = { draft: 'Draft', active: 'Active', archived: 'Archived' }
const STATUS_TONE = { draft: 'neutral', active: 'success', archived: 'warning' } as const

function splitRefs(v: string): string[] {
  return [...new Set(v.split(/[\s,]+/).map((x) => x.trim()).filter(Boolean))]
}

function useSkillWrite<B>(pathSuffix: string, onDone: () => void) {
  const { workspaceId } = useSession()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: B) =>
      apiFetch<SkillWriteResult>(`/workspaces/${workspaceId}/skills${pathSuffix}`, { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: (res) => {
      void qc.invalidateQueries({ queryKey: qk.skills(workspaceId) })
      // Keep the dialog open only to show warnings; otherwise close.
      if (!('warnings' in res) || res.warnings.length === 0) onDone()
    },
  })
}

function Warnings({ result, onClose }: { result: SkillWriteResult | undefined; onClose: () => void }) {
  if (!result || !('warnings' in result) || result.warnings.length === 0) return null
  return (
    <div role="status">
      <p className="ok-note">Saved <strong>{result.skill.slug}</strong> as a draft, with warnings:</p>
      <ul className="cell-sub">
        {result.warnings.map((w) => <li key={w}>{w}</li>)}
      </ul>
      <button type="button" className="btn btn--primary" onClick={onClose}>Done</button>
    </div>
  )
}

function CreateModal({ onClose }: { onClose: () => void }) {
  const create = useSkillWrite<SkillCreateRequest>('', onClose)
  const [slug, setSlug] = useState('')
  const [description, setDescription] = useState('')
  const [body, setBody] = useState('')
  const [tools, setTools] = useState('')
  const [toolkits, setToolkits] = useState('')
  const slugOk = SLUG_RE.test(slug) && slug.length <= 64
  const ready = slugOk && description.trim().length > 0
  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    if (!ready || create.isPending) return
    const tool_refs = splitRefs(tools)
    const toolkit_refs = splitRefs(toolkits)
    create.mutate({
      slug,
      description: description.trim(),
      body_md: body,
      ...(tool_refs.length ? { tool_refs } : {}),
      ...(toolkit_refs.length ? { toolkit_refs } : {}),
    })
  }
  return (
    <Modal title="New skill" onClose={onClose}>
      <Warnings result={create.data} onClose={onClose} />
      {!create.data && (
        <form onSubmit={onSubmit} aria-label="New skill form">
          <div className="field">
            <label htmlFor="sk-slug">Name (slug)</label>
            <input id="sk-slug" value={slug} onChange={(e) => setSlug(e.target.value)} maxLength={64} placeholder="e.g. weekly-report" />
            {slug && !slugOk && <span className="form-error" role="alert">Lowercase letters, digits and single hyphens.</span>}
          </div>
          <div className="field">
            <label htmlFor="sk-desc">Description</label>
            <input id="sk-desc" value={description} onChange={(e) => setDescription(e.target.value)} maxLength={1024} placeholder="When an agent should use this skill" />
          </div>
          <div className="field">
            <label htmlFor="sk-body">Instructions (Markdown)</label>
            <textarea id="sk-body" rows={8} value={body} onChange={(e) => setBody(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="sk-tools">Tools used (optional)</label>
            <input id="sk-tools" value={tools} onChange={(e) => setTools(e.target.value)} placeholder="e.g. slack_send_message" />
          </div>
          <div className="field">
            <label htmlFor="sk-toolkits">Toolkits used (optional)</label>
            <input id="sk-toolkits" value={toolkits} onChange={(e) => setToolkits(e.target.value)} placeholder="e.g. slack" />
          </div>
          {create.error ? <p className="form-error" role="alert">{errorMessage(create.error)}</p> : null}
          <div className="modal-foot modal-foot--inline">
            <button type="button" className="btn" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn--primary" disabled={!ready || create.isPending} aria-busy={create.isPending}>
              {create.isPending ? 'Creating…' : 'Create draft'}
            </button>
          </div>
        </form>
      )}
    </Modal>
  )
}

function ImportModal({ onClose }: { onClose: () => void }) {
  const imp = useSkillWrite<{ skill_md: string }>('/import', onClose)
  const [text, setText] = useState('')
  const [fileError, setFileError] = useState<string | null>(null)
  return (
    <Modal title="Import SKILL.md" onClose={onClose}>
      <Warnings result={imp.data} onClose={onClose} />
      {!imp.data && (
        <form
          aria-label="Import skill form"
          onSubmit={(e) => {
            e.preventDefault()
            if (text.trim() && !imp.isPending) imp.mutate({ skill_md: text })
          }}
        >
          <div className="field">
            <label htmlFor="sk-file">SKILL.md file</label>
            <input
              id="sk-file"
              type="file"
              accept=".md,text/markdown,text/plain"
              onChange={(e) => {
                setFileError(null)
                const file = e.target.files?.[0]
                if (!file) return
                file.text().then(setText, () => setFileError('Couldn’t read that file.'))
              }}
            />
            {fileError && <span className="form-error" role="alert">{fileError}</span>}
          </div>
          <div className="field">
            <label htmlFor="sk-md">Or paste it</label>
            <textarea id="sk-md" rows={10} value={text} onChange={(e) => setText(e.target.value)} placeholder={'---\nname: my-skill\ndescription: …\n---\n\nInstructions…'} />
            <span className="field-hint">A single SKILL.md with name and description frontmatter. Bundles aren’t supported.</span>
          </div>
          {imp.error ? <p className="form-error" role="alert">{errorMessage(imp.error)}</p> : null}
          <div className="modal-foot modal-foot--inline">
            <button type="button" className="btn" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn--primary" disabled={!text.trim() || imp.isPending} aria-busy={imp.isPending}>
              {imp.isPending ? 'Importing…' : 'Import'}
            </button>
          </div>
        </form>
      )}
    </Modal>
  )
}

function ForkModal({ skill, onClose }: { skill: SkillSummary; onClose: () => void }) {
  const fork = useSkillWrite<SkillForkRequest>('/fork', onClose)
  const [slug, setSlug] = useState('')
  const slugOk = !slug || (SLUG_RE.test(slug) && slug.length <= 64)
  return (
    <Modal title={`Fork ${skill.slug}`} onClose={onClose}>
      <Warnings result={fork.data} onClose={onClose} />
      {!fork.data && (
        <form
          aria-label="Fork skill form"
          onSubmit={(e) => {
            e.preventDefault()
            if (!slugOk || fork.isPending) return
            fork.mutate({ platform_skill_id: skill.id, ...(slug ? { slug } : {}) })
          }}
        >
          <p>Copy this platform skill into your workspace as a draft you can edit.</p>
          <div className="field">
            <label htmlFor="sk-fork-slug">New name (optional)</label>
            <input id="sk-fork-slug" value={slug} onChange={(e) => setSlug(e.target.value)} maxLength={64} placeholder={skill.slug} />
            {!slugOk && <span className="form-error" role="alert">Lowercase letters, digits and single hyphens.</span>}
          </div>
          {fork.error ? <p className="form-error" role="alert">{errorMessage(fork.error)}</p> : null}
          <div className="modal-foot modal-foot--inline">
            <button type="button" className="btn" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn--primary" disabled={!slugOk || fork.isPending} aria-busy={fork.isPending}>
              {fork.isPending ? 'Forking…' : 'Fork'}
            </button>
          </div>
        </form>
      )}
    </Modal>
  )
}

function StatusControl({ skill, canWrite, gate }: { skill: SkillSummary; canWrite: boolean; gate: Gate }) {
  const { workspaceId } = useSession()
  const qc = useQueryClient()
  const patch = useMutation({
    mutationFn: (status: SkillStatus) =>
      apiFetch(`/workspaces/${workspaceId}/skills/${skill.id}`, { method: 'PATCH', body: JSON.stringify({ status }) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.skills(workspaceId) }),
  })
  const badge = <Badge tone={STATUS_TONE[skill.status]}>{STATUS_LABEL[skill.status]}</Badge>
  if (!canWrite || skill.scope !== 'workspace') return badge
  return (
    <>
      <select
        aria-label={`Status of ${skill.slug}`}
        value={skill.status}
        disabled={!gate.allowed || patch.isPending}
        title={gate.reason || undefined}
        aria-busy={patch.isPending}
        onChange={(e) => {
          const next = e.target.value as SkillStatus
          if (next !== skill.status) patch.mutate(next)
        }}
      >
        <option value={skill.status}>{STATUS_LABEL[skill.status]}</option>
        {NEXT_STATUS[skill.status].map((s) => (
          <option key={s} value={s}>{STATUS_LABEL[s]}</option>
        ))}
      </select>
      {patch.error ? <div className="form-error" role="alert">{errorMessage(patch.error)}</div> : null}
    </>
  )
}

export function SkillsView() {
  const { workspaceId } = useSession()
  const canManage = useCan('manage_skills')
  const entitled = useFeature('skills.workspace')
  const [creating, setCreating] = useState(false)
  const [importing, setImporting] = useState(false)
  const [forking, setForking] = useState<SkillSummary | null>(null)
  const q = useQuery({
    queryKey: qk.skills(workspaceId),
    queryFn: () => apiFetch<SkillsResponse>(`/workspaces/${workspaceId}/skills?limit=100`),
    enabled: !!workspaceId,
  })
  const skills = q.data?.skills ?? []
  const own = skills.filter((s) => s.scope === 'workspace')
  const platform = skills.filter((s) => s.scope === 'platform')
  const exportHref = (s: SkillSummary) => apiUrl(`/workspaces/${workspaceId}/skills/${s.id}/export`)

  const table = (rows: SkillSummary[], label: string) => (
    <div className="table-wrap">
      <table className="table" aria-label={label}>
        <thead>
          <tr>
            <th>Skill</th>
            <th>Status</th>
            <th>Version</th>
            <th>Updated</th>
            <th><span className="sr-only">Actions</span></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((s) => (
            <tr key={s.id}>
              <td>
                <div className="cell-strong">{s.slug}</div>
                <div className="cell-sub">{s.description}</div>
              </td>
              <td><StatusControl skill={s} canWrite={canManage} gate={entitled} /></td>
              <td className="num">v{s.version}</td>
              <td>{formatDate(s.updated_at)}</td>
              <td className="cell-actions">
                <a className="btn btn--sm" href={exportHref(s)} download="SKILL.md" aria-label={`Export ${s.slug}`}>
                  Export
                </a>
                {canManage && s.scope === 'platform' && (
                  <button
                    type="button"
                    className="btn btn--sm"
                    disabled={!entitled.allowed}
                    title={entitled.reason || undefined}
                    onClick={() => setForking(s)}
                    aria-label={`Fork ${s.slug}`}
                  >
                    Fork
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )

  return (
    <div className="page">
      <PageHeader
        title="Skills"
        count={q.data ? own.length : undefined}
        description="Reusable playbooks that tell agents how to use your tools for a job. Skills are instructions only: they never run tools or grant access."
        actions={
          canManage && (
            <>
              <button type="button" className="btn" disabled={!entitled.allowed} onClick={() => setImporting(true)}>
                Import SKILL.md
              </button>
              <button type="button" className="btn btn--primary" disabled={!entitled.allowed} onClick={() => setCreating(true)}>
                <Icon name="plus" size={14} /> New skill
              </button>
            </>
          )
        }
      />
      {canManage && <PlanNote gate={entitled}>Workspace skills:</PlanNote>}

      <QueryState isLoading={q.isLoading} error={q.error} what="skills" onRetry={() => q.refetch()}>
        <section aria-labelledby="sk-own-h">
          <h2 id="sk-own-h">Workspace skills</h2>
          {own.length === 0 ? (
            <EmptyState icon="spark" title="No workspace skills yet" body="Write a skill, import a SKILL.md, or fork a platform skill below." />
          ) : (
            table(own, 'Workspace skills')
          )}
        </section>
        {platform.length > 0 && (
          <section aria-labelledby="sk-platform-h">
            <h2 id="sk-platform-h">Platform skills</h2>
            {table(platform, 'Platform skills')}
          </section>
        )}
        {q.data?.has_more && <p className="cell-sub">Showing the first 100 skills.</p>}
      </QueryState>

      {creating && <CreateModal onClose={() => setCreating(false)} />}
      {importing && <ImportModal onClose={() => setImporting(false)} />}
      {forking && <ForkModal skill={forking} onClose={() => setForking(null)} />}
    </div>
  )
}
